import { createSlice } from "@reduxjs/toolkit";

const pad2 = (n) => String(n).padStart(2, "0");

// Today's date in the user's LOCAL timezone as yyyy-MM-dd.
// (new Date().toISOString() is UTC - in India between 12:00am and 5:30am
// it returns YESTERDAY's date, which broke default dates and made the
// Settlement "To Date" exclude today's payments.)
export const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// Converts whatever date string the API sends into yyyy-MM-dd for
// <input type="date">. Handles: yyyy-MM-dd, yyyy-MM-ddTHH:mm:ss (.NET default),
// dd-MM-yyyy, dd/MM/yyyy, dd.MM.yyyy (each optionally followed by a time).
// Returns "" for empty / null / .NET min-date values.
export const toISODate = (val) => {
  if (!val) return "";
  const str = String(val).trim();
  let y, m, d, mt;
  if ((mt = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/))) {
    [, y, m, d] = mt;
  } else if ((mt = str.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/))) {
    [, d, m, y] = mt;
  } else {
    return "";
  }
  if (Number(y) < 1900) return "";
  return `${y}-${pad2(m)}-${pad2(d)}`;
};

// isCheque / isBanAcc = true means that input group is DISABLED.
// Cash: both locked. Cheque/DD: both open. Other (NEFT etc.): cheque locked, bank open.
export const payModeInputFlags = (mode) => {
  const iMode = Number(mode);
  if (iMode === 1) return { isCheque: true, isBanAcc: true };
  if (iMode === 2) return { isCheque: false, isBanAcc: false };
  return { isCheque: true, isBanAcc: false };
};

export const partyDisplayName = (objParty) => {
  if (objParty == null) return "";
  if (typeof objParty === "string" || typeof objParty === "number") {
    return String(objParty);
  }
  return (
    objParty.partyName ||
    objParty.strVenName ||
    objParty.strPartyName ||
    objParty.strSuppName ||
    objParty.strCustName ||
    objParty.strName ||
    ""
  );
};

const clearSettlementResult = (state) => {
  const ht = state.SettleMentHT;
  ht.iPayRecId = 0;
  ht.iVouchId = 0;
  ht.strVouchType = "";
  ht.strVouchNo = "";
  ht.dAmount = (0).toFixed(2);
  ht.dBalAmount = (0).toFixed(2);
  ht.dUnSettleAmount = (0).toFixed(2);
  ht.iHTCount = 0;
  ht.iDTCount = 0;
  state.SettleMentDT.arrHTdata = [];
  state.SettleMentDT.arrDTdata = [];
};

export const AccountsSlice = createSlice({
  name: "Account",
  initialState: {
    default: {
      iBranchId: 0,
      iScrId: 0,
      iPayModeId: 1,
      iVenTypeId: 1,
      iBankId: 0,
      iPayRecId: 0,
      strTransNo: "",
      strVouchNo: "",
      strVouchDate: todayLocal(),
      iVenId: 0,
      strVenName: "",
      strPrintName: "",
      strChequeNo: "",
      strChequeDate: todayLocal(),
      iBankAccId: 0,
      strBankName: "",
      strAccHolderName: "",
      strAccNo: "",
      dAmount: (0).toFixed(2),
      strRemark: "",
      strSearch: "",

      isCheque: true,
      isBanAcc: true,
      isCancelStatus: false,
    },
    HT: {
      arrNoFormat: [],
      arrPayment: [],
      arrPayMode: [],
      arrBankAcc: [],
      arrBankAccDetails: [],
      arrVenType: [],
      arrValidateProperties: [],
    },
    TabOrder: {
      VoucherTab: "",
      TransNoTab: "",
      TranDate: "",
      VouchDateTap: "",
      VenNameTab: "",
      VenTypeTab: "",
      PayModeTab: "",
      CheckNoTab: "",
      CheckDateTab: "",
      BankAccountTab: "",
      AccHolTab: "",
      AccNoTab: "",
      BankNameTab: "",
      AmountTab: "",
      PrintNameTab: "",
      Remarktab: "",
      PaymentListTab: "",
      PaymentFIlterTab: "",
      PaymentSearchTab: "",
      BtnSavtab: "",
      BtnCleartab: "",
      BtnDeletetab: "",
      BtnClosetab: "",
      BtnCancel: "",
      BtnAdjust: "",
    },
    DebitCredit: {
      iDebCrdId: 0,
      iScrId: 0,
      iBranchId: 0,
      strTransNo: "",
      strVouchNo: "",
      strVouchDate: todayLocal(),
      iVenTypeId: 1,
      iVenId: 0,
      strVenName: "",
      strRemark: "",
      dAmount: (0).toFixed(2),
      isCancel: false,
    },
    DebCrdHT: {
      arrNoFormat: [],
      arrEntHT: [],
      arrVenType: [],
      arrValidation: [],
    },
    DebCrdTab: {
      VoucherTab: "",
      TransNoTab: "",
      TranDate: "",
      VouchDateTap: "",
      VenNameTab: "",
      VenTypeTab: "",
      AmountTab: "",
      Remarktab: "",
      ArrTab: "",
      FilterTab: "",
      SearchTab: "",
      BtnSavtab: "",
      BtnCleartab: "",
      BtnDeletetab: "",
      BtnClosetab: "",
      BtnCancel: "",
    },
    SettleMentHT: {
      iScrId: 0,
      iPayRecId: 0,
      iVouchId: 0,
      iVenTypeId: 0,
      strVouchType: "",
      iVenId: 0,
      strVenName: "",
      strVouchNo: "",
      dAmount: (0).toFixed(2),
      dBalAmount: (0).toFixed(2),
      dUnSettleAmount: (0).toFixed(2),
      iHTCount: 0,
      iDTCount: 0,
      iVenScrId: 0,
      strVenScrCol: "",
      strFromDate: todayLocal(),
      strToDate: todayLocal(),
      isUnSettle: true,
    },
    SettleMentDT: {
      arrVenType: [],
      arrVouchType: [],
      arrHTdata: [],
      arrDTdata: [],
    },
  },
  reducers: {
    setAcc_default: (state, actions) => {
      state.default = actions.payload;
    },
    // Update ONE field of the voucher. Replaces the old pattern of rebuilding
    // the whole object from a stale render-time copy (which overwrote other
    // reducers' changes made in the same event).
    setAcc_field: (state, actions) => {
      state.default[actions.payload.key] = actions.payload.value;
    },
    // Change Vendor Type and clear the previously selected party in ONE step.
    setAcc_VenType: (state, actions) => {
      state.default.iVenTypeId = Number(actions.payload);
      state.default.iPayRecId = 0;
      state.default.iVenId = 0;
      state.default.strVenName = "";
      state.default.strPrintName = "";
      state.default.strRemark = "";
      state.default.strChequeNo = "";
      state.default.strAccNo = "";
      state.default.strAccHolderName = "";
      state.default.strBankName = "";
      state.default.iBankId = 0;
      state.default.dAmount = (0).toFixed(2);
    },
    setAcc_HT: (state, actions) => {
      state.HT = actions.payload;
    },
    setAcc_Party: (state, actions) => {
      state.default.strVenName = actions.payload.strVenName;
      state.default.iVenId = actions.payload.iVenId;
    },
    setAcc_PartyBank: (state, actions) => {
      state.default.iPayModeId = actions.payload.iPayModeId;
      state.default.strChequeNo = actions.payload.strChequeNo;
      state.default.strAccHolderName = actions.payload.strAccHolderName;
      state.default.iBankId = actions.payload.iBankId;
      state.default.strBankName = actions.payload.strBankName;
      state.default.strAccNo = actions.payload.strAccNo;
    },
    setAcc_BanKName: (state, actions) => {
      state.default.iBankId = actions.payload.iBankId;
      state.default.strBankName = actions.payload.strBankName;
    },
    setAcc_InpuPermission: (state, actions) => {
      state.default.isCheque = actions.payload.isCheque;
      state.default.isBanAcc = actions.payload.isBanAcc;
    },
    setAcc_Payment: (state, actions) => {
      state.HT.arrPayment = actions.payload;
    },
    setAcc_VouchNo: (state, actions) => {
      state.default.iBranchId = actions.payload.iBranchId;
      state.default.iScrId = actions.payload.iScrId;
      state.default.iVenTypeId = actions.payload.iVenTypeId;
      state.default.strTransNo = actions.payload.strTransNo;
      state.default.strVouchNo = actions.payload.strVouchNo;
      state.default.iBankAccId = actions.payload.iBankAccId;
    },
    setAcc_TabOrder: (state, actions) => {
      state.TabOrder = actions.payload;
    },
    refresh_Acc: (state) => {
      state.default = {
        iBranchId: 0,
        iScrId: 0,
        iPayModeId: 1,
        iVenTypeId: 1,
        iBankAccId: 0,
        iPayRecId: 0,
        strTransNo: "",
        strVouchNo: "",
        strVouchDate: todayLocal(),
        iVenId: 0,
        strVenName: "",
        strPrintName: "",
        strChequeNo: "",
        strChequeDate: todayLocal(),

        iBankId: 0,
        strBankName: "",
        strAccHolderName: "",
        strAccNo: "",
        dAmount: (0).toFixed(2),
        strRemark: "",
        strSearch: "",

        isCheque: true,
        isBanAcc: true,
        isCancelStatus: false,
      };
      state.HT = {
        arrNoFormat: [],
        arrPayment: [],
        arrPayMode: [],
        arrBankAcc: [],
        arrBankAccDetails: [],
        arrVenType: [],
        arrValidateProperties: [],
      };
      state.TabOrder = {
        VoucherTab: "",
        TransNoTab: "",
        TranDate: "",
        VouchDateTap: "",
        VenNameTab: "",
        VenTypeTab: "",
        PayModeTab: "",
        CheckNoTab: "",
        CheckDateTab: "",
        BankAccountTab: "",
        AccHolTab: "",
        AccNoTab: "",
        BankNameTab: "",
        AmountTab: "",
        PrintNameTab: "",
        Remarktab: "",
        PaymentListTab: "",
        PaymentFIlterTab: "",
        PaymentSearchTab: "",
        BtnSavtab: "",
        BtnCleartab: "",
        BtnDeletetab: "",
        BtnClosetab: "",
        BtnCancel: "",
        BtnAdjust: "",
      };
    },
    refresh_ParyAcc: (state) => {
      state.default.strChequeNo = "";
      state.default.strAccNo = "";
      state.default.strAccHolderName = "";
      state.default.strBankName = "";
      state.default.iBankId = 0;
    },
    refAcc_Party: (state) => {
      state.default.iPayRecId = 0;
      state.default.iVenId = 0;
      state.default.strVenName = "";
      state.default.strPrintName = "";
      state.default.strRemark = "";
      state.default.strChequeNo = "";
      state.default.strAccNo = "";
      state.default.strAccHolderName = "";
      state.default.strBankName = "";
      state.default.iBankId = 0;
      state.default.dAmount = (0).toFixed(2);
    },
    setDebCrdDeafult: (state, actions) => {
      state.DebitCredit = actions.payload;
    },
    setDebCrdVenType: (state, actions) => {
      state.DebitCredit.iVenTypeId = actions.payload;
    },
    setDebCrdHT: (state, actions) => {
      state.DebitCredit.iScrId = actions.payload.iScrId;
      state.DebitCredit.iBranchId = actions.payload.iBranchId;
      state.DebitCredit.iVenTypeId = actions.payload.iVenTypeId;
      state.DebitCredit.strTransNo = actions.payload.strTransNo;
      state.DebitCredit.strVouchNo = actions.payload.strVouchNo;
      state.DebCrdHT.arrNoFormat = actions.payload.arrNoFormat;
      state.DebCrdHT.arrEntHT = actions.payload.arrEntHT;
      state.DebCrdHT.arrVenType = actions.payload.arrVenType;
      state.DebCrdHT.arrValidation = actions.payload.arrValidation;
    },
    setEntDebCrdHT: (state, actions) => {
      state.DebCrdHT.arrEntHT = actions.payload;
    },
    setAccDeCr_party: (state, actions) => {
      state.DebitCredit.strVenName = actions.payload.strVenName;
      state.DebitCredit.iVenId = actions.payload.iVenId;
    },
    setAccDeCr_Taborder: (state, actions) => {
      state.DebCrdTab = actions.payload;
    },
    refresh_AccDebCrd: (state) => {
      state.DebitCredit = {
        iDebCrdId: 0,
        iScrId: 0,
        iBranchId: 0,
        strTransNo: "",
        strVouchNo: "",
        strVouchDate: todayLocal(),
        iVenTypeId: 1,
        iVenId: 0,
        strVenName: "",
        strRemark: "",
        dAmount: (0).toFixed(2),
        isCancel: false,
      };
      state.DebCrdHT = {
        arrNoFormat: [],
        arrEntHT: [],
        arrVenType: [],
        arrValidation: [],
      };
      state.DebCrdTab = {
        VoucherTab: "",
        TransNoTab: "",
        TranDate: "",
        VouchDateTap: "",
        VenNameTab: "",
        VenTypeTab: "",
        AmountTab: "",
        Remarktab: "",
        ArrTab: "",
        FilterTab: "",
        SearchTab: "",
        BtnSavtab: "",
        BtnCleartab: "",
        BtnDeletetab: "",
        BtnClosetab: "",
        BtnCancel: "",
      };
    },
    refresh_AccDeCrParty: (state) => {
      state.DebitCredit = {
        iVenId: 0,
        strVenName: "",
        strRemark: "",
        dAmount: (0).toFixed(2),
        isCancel: false,
      };
    },
    setSettlementDefault: (state, actions) => {
      state.SettleMentHT.iScrId = actions.payload.iScrId;
      state.SettleMentHT.iVenTypeId = actions.payload.iVenTypeId;
      state.SettleMentHT.iVenScrId = actions.payload.iVenScrId;
      state.SettleMentHT.strVenScrCol = actions.payload.strVenScrCol;
      state.SettleMentHT.strFromDate = actions.payload.strFromDate;
      state.SettleMentHT.strToDate = todayLocal();
      state.SettleMentDT.arrVenType = actions.payload.arrVenType;
      state.SettleMentDT.arrVouchType = actions.payload.arrVouchType;
    },
    setSettlementVoucher: (state, actions) => {
      state.SettleMentHT.iHTCount = actions.payload.iHTCount;
      state.SettleMentHT.iDTCount = actions.payload.iDTCount;
      state.SettleMentDT.arrHTdata = actions.payload.arrHTdata;
      state.SettleMentDT.arrDTdata = actions.payload.arrDTdata;
    },
    setSettlementVenParem: (state, actions) => {
      state.SettleMentHT.iScrId = actions.payload.iScrId;
      state.SettleMentHT.iVenScrId = actions.payload.iVenScrId;
      state.SettleMentHT.strVenScrCol = actions.payload.strVenScrCol;
      state.SettleMentHT.iVenTypeId = actions.payload.iVenTypeId;
      // a different vendor type invalidates the chosen vendor and the lists
      state.SettleMentHT.iVenId = 0;
      state.SettleMentHT.strVenName = "";
      clearSettlementResult(state);
    },
    clear_SettlementResult: (state) => {
      clearSettlementResult(state);
    },
    setSettlementVendor: (state, actions) => {
      state.SettleMentHT.iVenId = actions.payload.iVenId;
      state.SettleMentHT.strVenName = actions.payload.strVenName;
    },
    setsettlementtab: (state, actions) => {
      state.SettleMentHT.isUnSettle = actions.payload;
    },
    setsettlementfromdate: (state, actions) => {
      state.SettleMentHT.strFromDate = actions.payload;
    },
    setsettlementtodate: (state, actions) => {
      state.SettleMentHT.strToDate = actions.payload;
    },
    setsettlementvouchtype: (state, actions) => {
      state.SettleMentHT.iScrId = actions.payload;
    },
    setsettlementdAmount: (state, actions) => {
      state.SettleMentHT.dAmount = actions.payload;
    },
    setselectedvoucher: (state, actions) => {
      state.SettleMentHT.iVouchId = actions.payload.iVouchId;
      state.SettleMentHT.strVouchNo = actions.payload.strVouchNo;
      state.SettleMentHT.dUnSettleAmount = actions.payload.dUnSettleAmount;
      state.SettleMentHT.strVouchType = actions.payload.strVouchType;
      state.SettleMentHT.dAmount = actions.payload.dAmount;
    },
    setselectedpayrec: (state, actions) => {
      state.SettleMentHT.iPayRecId = actions.payload.iPayRecId;
      state.SettleMentHT.dBalAmount = actions.payload.dBalAmount;
      state.SettleMentDT.arrDTdata = actions.payload.arrDTdata;
    },
    setnewsettlment: (state, actions) => {
      state.SettleMentDT.arrDTdata = actions.payload.arrDTdata;
      state.SettleMentHT.dBalAmount = actions.payload.dBalAmount;
    },
    refresh_Settlement: (state) => {
      state.SettleMentHT = {
        iScrId: 0,
        iPayRecId: 0,
        iVouchId: 0,
        iVenTypeId: 0,
        strVouchType: "",
        iVenId: 0,
        strVenName: "",
        strVouchNo: "",
        dAmount: (0).toFixed(2),
        dBalAmount: (0).toFixed(2),
        dUnSettleAmount: (0).toFixed(2),
        iHTCount: 0,
        iDTCount: 0,
        iVenScrId: 0,
        strVenScrCol: "",
        strFromDate: todayLocal(),
        strToDate: todayLocal(),
        isUnSettle: true,
      };
      state.SettleMentDT = {
        arrVenType: [],
        arrVouchType: [],
        arrHTdata: [],
        arrDTdata: [],
      };
    },
    refresh_Settlementdt: (state) => {
      // after Adjust, forget the selected voucher too so the same voucher
      // cannot be adjusted again by mistake
      state.SettleMentHT.iVouchId = 0;
      state.SettleMentHT.strVouchType = "";
      state.SettleMentHT.strVouchNo = "";
      state.SettleMentHT.dAmount = (0).toFixed(2);
      state.SettleMentHT.dUnSettleAmount = (0).toFixed(2);
    },
    refresh_Settlementtab: (state) => {
      state.SettleMentHT.iScrId = 0;
      state.SettleMentHT.iPayRecId = 0;
      state.SettleMentHT.iVouchId = 0;
      state.SettleMentHT.iVenTypeId = 0;
      state.SettleMentHT.strVouchType = "";
      state.SettleMentHT.strVenName = "";
      state.SettleMentHT.iVenId = 0;
      state.SettleMentHT.strVouchNo = "";
      state.SettleMentHT.dAmount = 0;
      state.SettleMentHT.dBalAmount = 0;
      state.SettleMentHT.dUnSettleAmount = 0;
      state.SettleMentHT.iHTCount = 0;
      state.SettleMentHT.iDTCount = 0;
      state.SettleMentHT.iVenScrId = 0;
      state.SettleMentHT.strVenScrCol = "";
      state.SettleMentDT.arrHTdata = [];
      state.SettleMentDT.arrDTdata = [];
    },
  },
});
export const {
  setAcc_default,
  setAcc_field,
  setAcc_VenType,
  setAcc_HT,
  setAcc_Party,
  setAcc_BanKName,
  setAcc_VouchNo,
  setAcc_InpuPermission,
  setAcc_PartyBank,
  setAcc_TabOrder,
  setAcc_Payment,
  refresh_ParyAcc,
  refresh_Acc,
  refAcc_Party,
  setDebCrdDeafult,
  setDebCrdHT,
  setEntDebCrdHT,
  refresh_AccDebCrd,
  setDebCrdVenType,
  refresh_AccDeCrParty,
  setAccDeCr_party,
  setAccDeCr_Taborder,
  setSettlementDefault,
  setSettlementVenParem,
  setSettlementVendor,
  setsettlementtab,
  setsettlementfromdate,
  setsettlementtodate,
  setSettlementVoucher,
  setsettlementvouchtype,
  setselectedvoucher,
  setsettlementdAmount,
  setselectedpayrec,
  setnewsettlment,
  clear_SettlementResult,
  refresh_Settlement,
  refresh_Settlementdt,
  refresh_Settlementtab,
} = AccountsSlice.actions;
export default AccountsSlice.reducer;